function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, SEMANTIC_ATTRIBUTE_SENTRY_OP } from './semanticAttributes.js';
import './tracing/errors.js';
import { isInstanceOf } from './utils-hoist/is.js';
import './utils-hoist/debug-build.js';
import './utils-hoist/logger.js';
import './utils-hoist/time.js';
import './utils-hoist/syncpromise.js';
import { SENTRY_BAGGAGE_KEY_PREFIX } from './utils-hoist/baggage.js';
import { parseUrl } from './utils-hoist/url.js';
import './debug-build.js';
import { hasSpansEnabled } from './utils/hasSpansEnabled.js';
import { getActiveSpan } from './utils/spanUtils.js';
import { SentryNonRecordingSpan } from './tracing/sentryNonRecordingSpan.js';
import { setHttpStatus, SPAN_STATUS_ERROR } from './tracing/spanstatus.js';
import { startInactiveSpan } from './tracing/trace.js';
import { getTraceData } from './utils/traceData.js';
/**
 * Create and track fetch request spans for usage in combination with `addFetchInstrumentationHandler`.
 *
 * @returns Span if a span was created, otherwise void.
 */

function instrumentFetchRequest(handlerData, shouldCreateSpan, shouldAttachHeaders, spans) {
  var spanOrigin = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 'auto.http.browser';

  if (!handlerData.fetchData) {
    return undefined;
  }

  var shouldCreateSpanResult = hasSpansEnabled() && shouldCreateSpan(handlerData.fetchData.url);

  if (handlerData.endTimestamp && shouldCreateSpanResult) {
    var spanId = handlerData.fetchData.__span;
    if (!spanId) return;
    var _span = spans[spanId];

    if (_span) {
      endSpan(_span, handlerData); // eslint-disable-next-line @typescript-eslint/no-dynamic-delete

      delete spans[spanId];
    }

    return undefined;
  }

  var {
    method,
    url
  } = handlerData.fetchData;
  var fullUrl = getFullURL(url);
  var host = fullUrl ? parseUrl(fullUrl).host : undefined;
  var hasParent = !!getActiveSpan();
  var span = shouldCreateSpanResult && hasParent ? startInactiveSpan({
    name: "".concat(method, " ").concat(url),
    attributes: {
      url,
      type: 'fetch',
      'http.method': method,
      'http.url': fullUrl,
      'server.address': host,
      [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: spanOrigin,
      [SEMANTIC_ATTRIBUTE_SENTRY_OP]: 'http.client'
    }
  }) : new SentryNonRecordingSpan();
  handlerData.fetchData.__span = span.spanContext().spanId;
  spans[span.spanContext().spanId] = span;

  if (shouldAttachHeaders(handlerData.fetchData.url)) {
    var request = handlerData.args[0];
    var options = handlerData.args[1] || {};

    var headers = _addTracingHeadersToFetchRequest(request, options, // If performance is disabled (TWP) or there's no active root span (pageload/navigation/interaction),
    // we do not want to use the span as base for the trace headers,
    // which means that the headers will be generated from the scope and the sampling decision is deferred
    hasSpansEnabled() && hasParent ? span : undefined);

    if (headers) {
      // Ensure this is actually set, if no options have been passed previously
      handlerData.args[1] = options;
      options.headers = headers;
    }
  }

  return span;
}
/**
 * Adds sentry-trace and baggage headers to the various forms of fetch headers.
 */


function _addTracingHeadersToFetchRequest(request, fetchOptionsObj, span) {
  var traceHeaders = getTraceData({
    span
  });
  var sentryTrace = traceHeaders['sentry-trace'];
  var baggage = traceHeaders.baggage; // Nothing to do, when we return undefined here, the original headers will be used

  if (!sentryTrace) {
    return undefined;
  }

  var headers = fetchOptionsObj.headers || (isRequest(request) ? request.headers : undefined);

  if (!headers) {
    return _objectSpread({}, traceHeaders);
  } else if (isHeaders(headers)) {
    var newHeaders = new Headers(headers);
    newHeaders.set('sentry-trace', sentryTrace);

    if (baggage) {
      var prevBaggageHeader = newHeaders.get('baggage');

      if (prevBaggageHeader) {
        var prevHeaderStrippedFromSentryBaggage = stripBaggageHeaderOfSentryBaggageValues(prevBaggageHeader);
        newHeaders.set('baggage', // If there are non-sentry entries (i.e. if the stripped string is non-empty/truthy) combine the stripped header and sentry baggage header
        // otherwise just set the sentry baggage header
        prevHeaderStrippedFromSentryBaggage ? "".concat(prevHeaderStrippedFromSentryBaggage, ",").concat(baggage) : baggage);
      } else {
        newHeaders.set('baggage', baggage);
      }
    }

    return newHeaders;
  } else if (Array.isArray(headers)) {
    var _newHeaders = [...headers // Remove any existing sentry-trace headers
    .filter(header => {
      return !(Array.isArray(header) && header[0] === 'sentry-trace');
    }) // Get rid of previous sentry baggage values in baggage header
    .map(header => {
      if (Array.isArray(header) && header[0] === 'baggage' && typeof header[1] === 'string') {
        var [headerName, headerValue, ...rest] = header;
        return [headerName, stripBaggageHeaderOfSentryBaggageValues(headerValue), ...rest];
      } else {
        return header;
      }
    }), // Attach the new sentry-trace header
    ['sentry-trace', sentryTrace]];

    if (baggage) {
      // If there are multiple entries with the same key, the browser will merge the values into a single request header.
      // Its therefore safe to simply push a "baggage" entry, even though there might already be another baggage header.
      _newHeaders.push(['baggage', baggage]);
    }

    return _newHeaders;
  } else {
    var existingBaggageHeader = 'baggage' in headers ? headers.baggage : undefined;
    var newBaggageHeaders = [];

    if (Array.isArray(existingBaggageHeader)) {
      newBaggageHeaders = existingBaggageHeader.map(headerItem => typeof headerItem === 'string' ? stripBaggageHeaderOfSentryBaggageValues(headerItem) : headerItem).filter(headerItem => headerItem === '');
    } else if (existingBaggageHeader) {
      newBaggageHeaders.push(stripBaggageHeaderOfSentryBaggageValues(existingBaggageHeader));
    }

    if (baggage) {
      newBaggageHeaders.push(baggage);
    }

    return _objectSpread(_objectSpread({}, headers), {}, {
      'sentry-trace': sentryTrace,
      baggage: newBaggageHeaders.length > 0 ? newBaggageHeaders.join(',') : undefined
    });
  }
}

function getFullURL(url) {
  try {
    var parsed = new URL(url);
    return parsed.href;
  } catch (_unused) {
    return undefined;
  }
}

function endSpan(span, handlerData) {
  if (handlerData.response) {
    var _handlerData$response;

    setHttpStatus(span, handlerData.response.status);
    var contentLength = ((_handlerData$response = handlerData.response) === null || _handlerData$response === void 0 ? void 0 : _handlerData$response.headers) && handlerData.response.headers.get('content-length');

    if (contentLength) {
      var contentLengthNum = parseInt(contentLength);

      if (contentLengthNum > 0) {
        span.setAttribute('http.response_content_length', contentLengthNum);
      }
    }
  } else if (handlerData.error) {
    span.setStatus({
      code: SPAN_STATUS_ERROR,
      message: 'internal_error'
    });
  }

  span.end();
}

function stripBaggageHeaderOfSentryBaggageValues(baggageHeader) {
  return baggageHeader.split(',') // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  .filter(baggageEntry => !baggageEntry.split('=')[0].startsWith(SENTRY_BAGGAGE_KEY_PREFIX)).join(',');
}

function isRequest(request) {
  return typeof Request !== 'undefined' && isInstanceOf(request, Request);
}

function isHeaders(headers) {
  return typeof Headers !== 'undefined' && isInstanceOf(headers, Headers);
}

export { instrumentFetchRequest };